import React from 'react'
import PropTypes from 'prop-types'
import {Box, Flex, Text, useMediaQuery, useMultiStyleConfig, useStyleConfig} from '@chakra-ui/react'
import KeyFeaturesCard from './key-features-card'
import {Swiper, SwiperSlide} from 'swiper/react'
import {Mousewheel, FreeMode} from 'swiper/modules'
import {breakpoints, remCalc} from '../../../theme/_utils'
import {getGlobalLink} from '../../../utils/contentstack-utils'
import {useGetCategoriesData} from '../../../custom-api/hooks/use-get-categories-data'
import {useProducts} from '@salesforce/commerce-sdk-react'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {FormattedMessage} from 'react-intl'
import useSlideUpAnimation from '../../../custom-hooks/use-slide-up-animation'

const KeyFeatures = ({categoryName, cards, isSlider = false, aspectRatio}) => {
    const [isDesktop] = useMediaQuery(`(min-width: ${breakpoints.xl})`)
    const {containerRef, getAnimProps} = useSlideUpAnimation()

    const categoriesData = useGetCategoriesData()
    const ids = cards
        ?.filter((c) => c.button?.productsfcc?.data?.length > 0)
        ?.map((c) => c.button.productsfcc.data[0].id)
        ?.filter(Boolean)

    const {data: productsData} = useProducts(
        {
            parameters: {
                ids: ids?.join(','),
                expand: ['availability', 'images'],
            },
            headers: {
                'x-debug': 'key-features-index',
            },
        },
        {enabled: ids?.length > 0},
    )

    const swiperStyles = useStyleConfig('Swiper')
    const styles = useMultiStyleConfig('KeyFeatures', {
        variant: isSlider ? 'slider' : 'default',
    })

    const {locale} = useMultiSite()

    return (
        <Flex {...styles.container} ref={containerRef} data-cs-label="editorial-key_features">
            <Flex {...styles.headerGrid}>
                <Text {...styles.title} as="h2">
                    <FormattedMessage
                        id="key_features.labels.title"
                        defaultMessage="Product Details"
                    />
                </Text>
                <Text {...styles.description}>
                    {categoryName ? (
                        <FormattedMessage
                            id="key_features.labels.description.pdp"
                            defaultMessage="What makes {categoryName} special"
                            values={{categoryName}}
                        />
                    ) : (
                        <FormattedMessage
                            id="key_features.labels.description.default"
                            defaultMessage="What makes this special"
                            // To maintain backward compatibility with old translation, adding a fallback for the productName variable to null
                            values={{productName: null}}
                        />
                    )}
                </Text>
            </Flex>
            {isSlider && !isDesktop ? (
                <Box
                    __css={{
                        ...swiperStyles,
                        '.swiper-slide': {
                            ...swiperStyles['.swiper-slide'],
                            height: 'auto',
                            width: {base: remCalc(280), md: remCalc(352)},
                        },
                    }}
                >
                    <Swiper
                        modules={[Mousewheel, FreeMode]}
                        slidesPerView="auto"
                        spaceBetween={6}
                        freeMode={true}
                        className="key-features-slider"
                        mousewheel={{forceToAxis: true, sensitivity: 1, releaseOnEdges: true}}
                    >
                        {cards &&
                            cards.map((card, index) => (
                                <SwiperSlide key={card.uid}>
                                    <Box {...getAnimProps(index)} height="100%" width="100%">
                                        <KeyFeaturesCard
                                            imageUrl={card?.image?.url}
                                            isSlider
                                            title={card?.header_copy}
                                            description={card?.body_copy}
                                            aspectRatio={aspectRatio}
                                            iconId={card?.uid}
                                        />
                                    </Box>
                                </SwiperSlide>
                            ))}
                    </Swiper>
                </Box>
            ) : (
                <Flex {...styles.cardsContainer}>
                    {cards &&
                        cards.map((card, index) => {
                            const {url, label, isAbsolute} = getGlobalLink({
                                link: card?.button,
                                categoriesData,
                                productsData,
                                locale: locale?.alias || locale?.id?.toLowerCase(),
                            })

                            return (
                                <Box key={card.uid} {...getAnimProps(index)}>
                                    <KeyFeaturesCard
                                        isLinkAbsolute={isAbsolute}
                                        imageUrl={card?.image?.url}
                                        images={card?.images}
                                        title={card?.header_copy}
                                        description={card?.body_copy}
                                        linkLabel={label}
                                        linkUrl={url}
                                        aspectRatio={aspectRatio}
                                        iconId={card?.uid}
                                    />
                                </Box>
                            )
                        })}
                </Flex>
            )}
        </Flex>
    )
}

KeyFeatures.displayName = 'KeyFeatures'

KeyFeatures.propTypes = {
    categoryName: PropTypes.string,
    cards: PropTypes.array,
    isSlider: PropTypes.bool,
    aspectRatio: PropTypes.string,
}

export default KeyFeatures
